import { fetchApi } from "@/lib/api/index.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";

export enum RiskLevel {
  LOW = "low",
  MODERATE = "moderate",
  HIGH = "high",
}

export interface TriggerExample {
  id: string;
  triggerId: string;
  example: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface TriggerKnowledgeBase {
  id: string;
  title: string;
  triggerExamples: TriggerExample[];
  context: string;
  instances: string;
  suggested_actions: string;
  risk_level: RiskLevel;
  category: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

type TriggerKnowledgeBaseUpdateData = Partial<
  Omit<TriggerKnowledgeBase, "id" | "created_at" | "updated_at" | "metadata">
>;

interface TriggerKnowledgeBaseResponse {
  triggers: TriggerKnowledgeBase[];
  hasMore: boolean;
}

export const useTriggerKnowledgeBase = (
  page: number,
  perPage: number,
  search?: string,
  enabled = true,
) => {
  return useQuery<TriggerKnowledgeBaseResponse>({
    queryKey: ["triggers-knowledge-base", page, perPage, search],
    queryFn: async () => {
      const response = await fetchApi<TriggerKnowledgeBaseResponse>({
        url: "/v2/triggers-knowledge-base/list",
        method: "GET",
        params: {
          page,
          limit: perPage,
          search,
        },
      });

      if (!response.data) {
        throw new Error("No data returned from API");
      }

      return {
        triggers: response.data.triggers,
        hasMore: response.data.hasMore,
      };
    },
    enabled,
  });
};

export const useGetTriggerById = (triggerId: string) => {
  return useQuery({
    queryKey: ["trigger", triggerId],
    queryFn: async () => {
      const response = await fetchApi<TriggerKnowledgeBase>({
        url: `/v2/triggers-knowledge-base/${triggerId}`,
        method: "GET",
      });

      if (!response.data) {
        throw new Error("No data returned from API");
      }

      return response.data;
    },
    enabled: !!triggerId,
  });
};

export const useCreateTrigger = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (
      data: Omit<
        TriggerKnowledgeBase,
        "id" | "created_at" | "updated_at" | "metadata" | "triggerExamples"
      > & { examples: string },
    ) => {
      const response = await fetchApi<{ trigger: TriggerKnowledgeBase }>({
        url: "/v2/triggers-knowledge-base",
        method: "POST",
        data,
      });

      return response.data.trigger;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["triggers-knowledge-base"] });
      toast({
        title: "Success",
        description: "Trigger created successfully",
      });
    },
    onError: (error) => {
      console.error("Failed to create trigger:", error);
      toast({
        title: "Error",
        description: "Failed to create trigger. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateTrigger = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      triggerId,
      data,
    }: {
      triggerId: string;
      data: TriggerKnowledgeBaseUpdateData;
    }) => {
      const response = await fetchApi<{ trigger: TriggerKnowledgeBase }>({
        url: `/v2/triggers-knowledge-base/${triggerId}`,
        method: "PUT",
        data,
      });

      return response.data.trigger;
    },
    onSuccess: (_, { triggerId }) => {
      queryClient.invalidateQueries({ queryKey: ["triggers-knowledge-base"] });
      queryClient.invalidateQueries({ queryKey: ["trigger", triggerId] });
      toast({
        title: "Success",
        description: "Trigger updated successfully",
      });
    },
    onError: (error) => {
      console.error("Failed to update trigger:", error);
      toast({
        title: "Error",
        description: "Failed to update trigger. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteTrigger = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (triggerId: string) => {
      await fetchApi({
        url: `/v2/triggers-knowledge-base/${triggerId}`,
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["triggers-knowledge-base"] });
      toast({
        title: "Success",
        description: "Trigger deleted successfully",
      });
    },
    onError: (error) => {
      console.error("Failed to delete trigger:", error);
      toast({
        title: "Error",
        description: "Failed to delete trigger. Please try again.",
        variant: "destructive",
      });
    },
  });
};
